import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Zoom, Scrollbar, Mousewheel } from "swiper/modules";
import { PiFlagPennant } from "react-icons/pi";
import { ArrowRightToLine, X, PanelRight, ChevronLeft, ChevronRight, ChevronDown, ZoomIn, ZoomOut, Shrink, Captions, EyeOff, Info } from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/zoom";
import "swiper/css/scrollbar";
import "swiper/css/mousewheel";
import AnalyticsService from "@/services/analytics";

// Define interface for image items with tags
interface ImageItem {
  src: string;
  alt?: string;
  tag_name?: string;
  category_name?: string;
  qtags?: Array<{
    tagId: number;
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  tags?: Array<{
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  // Display sections for accessibility tags
  displaySections?: Record<string, string>;
  // Venue type to determine rendering behavior
  venueType?: 'Basic' | 'Hybrid';
  // Other properties from review photos
  path?: string;
  review_photos_id?: string | number;
  user_name?: string;
  id?: number;
  tag_applied?: string;
  created_at?: string;
  photo_type?: string;
  width?: number;
  height?: number;
  sort_order?: number;
  // Venue details for tracking
  venueId?: string | number;
  venueName?: string;
  venueAddress?: string;
  venueBrandId?: string | number;
  venueBrandName?: string;
  venueTypes?: string;
}

interface FullImageViewerProps {
  images: ImageItem[];
  currentIndex: number;
  onClose: () => void;
  onFlag: (image: ImageItem) => void;
  showFlag: boolean;
  showRightTab: boolean;
}

// Reusable component for the image details tab content
const ImageDetailsTab = ({ currentImage }: { currentImage: ImageItem }) => (
  <div className="min-h-[200px]" role="region" aria-label="Image description details">
    {/* Alt text section */}
    {currentImage?.alt && (
      <div className="mb-4 bg-black bg-opacity-30 rounded-md">
        <h3 className="font-semibold text-md mb-2 text-primary-100">Description</h3>
        <div className="max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
          <p className="text-primary-100 text-sm transport-options-content-slider" tabIndex={0}
            dangerouslySetInnerHTML={{ 
              __html: currentImage.alt ?
                currentImage.alt.replace(/\r\n|\r|\n/g, '<br />') : ''
            }}
          />
        </div>
        <div className="text-xs text-primary-100 mt-2 text-left">
          {currentImage?.created_at && (
            <>
              {new Date(currentImage.created_at).toLocaleDateString()}<span className="mx-2">•</span>
            </>
          )}
          Sociability Team
        </div>
      </div>
    )}
    
    {/* Photo metadata */}
    <div className="mt-6 pt-4 border-t border-gray-800 text-xs text-gray-500">
      {currentImage?.user_name && <p className="mb-1">Added by: {currentImage.user_name}</p>}
    </div>
  </div>
);

// Reusable component for the accessibility tags tab content
const AccessibilityTagsTab = ({ currentImage }: { currentImage: ImageItem }) => (
  <div className="min-h-[200px]"  >
    {/* Accessibility tags section */}
    {currentImage?.qtags && currentImage.qtags.length > 0 ? (
      <div className="mb-4" tabIndex={0} aria-label="Accessibility tags" role="region">
        <div>
          {(() => {
            // Group tags by their display section
            const sections = currentImage.displaySections || {};
            const sectionMap = new Map<string, string[]>();
            
            // First, organize tags by section
            currentImage.qtags.forEach(tag => {
              const sectionId = tag.categoryDisplaySectionId?.toString() || '';
              const sectionName = sections[sectionId] || 'Other';
              
              if (!sectionMap.has(sectionName)) {
                sectionMap.set(sectionName, []);
              }
              
              sectionMap.get(sectionName)?.push(tag.tagName);
            });
            
            // Get the main tag (first tag)
            const mainTag = currentImage.qtags[0]?.tagName || '';
            
            // Now render the sections
            return Array.from(sectionMap.entries()).map(([sectionName, tagNames], sectionIndex) => {
              // Check if all values match the main tag
              const allValuesMatchMainTag = tagNames.every(value => value === mainTag);
              
              return (
                <div
                  key={sectionName}
                  className={`px-[18px] ${sectionIndex !== sectionMap.size - 1 ? "border-b border-b-gray-500" : ""}`}
                  role="region"
                  aria-label={`${sectionName} accessibility features`}
                  tabIndex={0}  
                >
                  {/* Only show section name if not all values match main tag */}
                  {!allValuesMatchMainTag && (
                    <p className="text-primary-300 dark:text-primary-300 font-medium pt-2" tabIndex={0}>
                      {sectionName === "Main Area" ? "General" : `${sectionName}:`}
                    </p>
                  )}
                  
                  {/* List the tags */}
                  <ul className="list-disc px-6 py-1" role="list">
                    {tagNames.map((tagName, tagIndex) => {
                      // For Basic venues: Skip if this tag is the main tag
                      // For Hybrid venues: Always show all tags
                      if (currentImage.venueType === 'Basic' && mainTag === tagName) return null;
                      
                      return (
                        <li key={tagIndex} className="text-primary-100 my-1" tabIndex={0}>
                          {tagName}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            });
          })()} 
        </div>
      </div>
    ) : (
      <div className="text-center py-6 text-primary-100/70" tabIndex={0} aria-label="No accessibility tags available for this photo." role="region">
        <p>No accessibility tags available for this photo.</p>
      </div>
    )}
  </div>
);

// Add this near the top of the component after the imports
const CustomScrollbarStyle = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(163, 159, 232, 0.6);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(163, 159, 232, 0.8);
    }
  `}} />
);

export default function FullImageViewer({ images, currentIndex, onClose, onFlag, showFlag, showRightTab }: FullImageViewerProps) {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'accessibility'>('accessibility');
  const [currentZoomLevel, setCurrentZoomLevel] = useState(1);
  const [showAltText, setShowAltText] = useState(false);

  // Add refs for focus management
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  // Add ref for sidebar
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);

  const currentImage = images[activeIndex];

  // Common tracking function with venue and image details
  const trackEvent = (eventName: string, additionalProps: Record<string, any> = {}) => {
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {
      // Venue details
      VenueId: currentImage?.venueId,
      VenueName: currentImage?.venueName,
      VenueAddress: currentImage?.venueAddress,
      VenueBrandId: currentImage?.venueBrandId,
      VenueBrandName: currentImage?.venueBrandName,
      VenueTypes: currentImage?.venueTypes,
      // Image details
      PhotoId: currentImage?.review_photos_id,
      PhotoCategory: currentImage?.category_name,
      PhotoTag: currentImage?.tag_name,
      HasAltText: Boolean(currentImage?.alt),
      PhotoIndex: activeIndex,
      TotalPhotos: images.length,
      // Additional properties
      ...additionalProps
    });
  };

  // Focus management when modal opens/closes
  useEffect(() => {
    // Store the element that had focus before the modal opened
    lastFocusedElement.current = document.activeElement as HTMLElement;

    // Focus the modal container
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // Hide the main content from screen readers
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    }

    // Add event listener for Escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Set all focusable elements outside the modal to tabindex="-1"
    const focusableElements = Array.from(
      document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ) as HTMLElement[];

    // Store original tabindex values to restore them later
    const originalTabIndexes = new Map<HTMLElement, string | null>();

    // Make all elements outside the modal non-focusable
    focusableElements.forEach(el => {
      if (!modalRef.current?.contains(el)) {
        originalTabIndexes.set(el, el.getAttribute('tabindex'));
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      }
    });

    return () => {
      // Clean up
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restore focus to the element that had focus before the modal opened
      if (lastFocusedElement.current) {
        lastFocusedElement.current.focus();
      }

      // Remove aria-hidden from main content
      if (mainContent) {
        mainContent.removeAttribute('aria-hidden');
      }

      // Restore original tabindex values
      originalTabIndexes.forEach((value, element) => {
        if (value === null) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', value);
        }
        element.removeAttribute('aria-hidden');
      });
    };
  }, [onClose]);

  // Handle tab key to trap focus within the modal more reliably
  const handleTabKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !modalRef.current) return;

    // Get all focusable elements - expanding the selector to ensure we catch all possible elements
    const focusableElementsSelector = 
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable=true]';
    
    const focusableElements = Array.from(
      modalRef.current.querySelectorAll(focusableElementsSelector)
    ).filter(el => {
      // Additional check to make sure the element is visible
      const style = window.getComputedStyle(el as Element);
      return style.display !== 'none' && style.visibility !== 'hidden' && (el as HTMLElement).offsetWidth > 0;
    }) as HTMLElement[];

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Handle regular and shift+tab navigation
    if (e.shiftKey) {
      if (document.activeElement === firstElement || !modalRef.current.contains(document.activeElement)) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement || !modalRef.current.contains(document.activeElement)) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    const newSidebarState = !showDetails;
    setShowDetails(newSidebarState);
    
    // Track sidebar toggle with updated event names
    trackEvent(newSidebarState ? "viewedPhotoInfo" : "hidePhotoInfo");
    
    // Focus management for sidebar
    if (!showDetails) {
      // When opening sidebar, focus the first interactive element in the sidebar
      setTimeout(() => {
        const firstTab = sidebarRef.current?.querySelector('button');
        if (firstTab) {
          firstTab.focus();
        }
      }, 100);
    } else {
      // When closing sidebar, focus the toggle button
      if (sidebarToggleRef.current) {
        sidebarToggleRef.current.focus();
      }
    }
  };

  // Initialize thumbnail swiper to center on the current image when component loads
  useEffect(() => {
    // Wait for thumbsSwiper to be initialized
    if (thumbsSwiper && thumbsSwiper.slideTo) {
      // Use a small timeout to ensure the DOM has fully rendered
      setTimeout(() => {
        // Center the thumbnail swiper on the current image
        thumbsSwiper.slideTo(currentIndex);
      }, 100);
    }
  }, [thumbsSwiper, currentIndex]);

  // Handle tab change
  const handleTabChange = (tab: 'details' | 'accessibility') => {
    setActiveTab(tab);
    
    // Track tab change with updated event names
    trackEvent(tab === 'details' ? "viewedPhotoAltTextTab" : "viewedPhotoTagsTab");
    
    // Focus the content of the selected tab
    setTimeout(() => {
      const tabPanel = document.getElementById(`${tab}-tabpanel`);
      if (tabPanel) {
        tabPanel.focus();
      }
    }, 100);
  };

  return (
    <div
      ref={modalRef}
      className={`fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col transition-all duration-300 ${showDetails ? "md:pb-0" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Image Viewer"
      onKeyDown={handleTabKey}
      tabIndex={-1}
    >
      <CustomScrollbarStyle />
      {/* Top left bar */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <button
          ref={closeButtonRef}
          className="flex items-center justify-center text-primary-100 p-2 bg-black md:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
          onClick={() => {
            // Track closing the lightbox
            trackEvent("exitedPhotoLightbox");
            onClose();
          }}
          aria-label="Close image viewer"
          tabIndex={0}
        >
          <X size={20} className="text-primary-100" aria-hidden="true" />
        </button>
      </div>

      {/* Top right bar - only visible on desktop */}
      {showRightTab && (
        <div className="absolute top-4 right-4 z-10 hidden md:block">
          {!showDetails ? (
            <div className="flex items-center gap-2">
              <span className="py-[10px] px-[8px] whitespace-nowrap transition-colors text-sm text-primary-100">
                Accessibility info
              </span>
              <button
                ref={sidebarToggleRef}
                onClick={handleSidebarToggle}
                className="flex items-center justify-center text-primary-100 p-2 bg-gray-700 rounded-md"
                aria-label="Show accessibility information"
                aria-expanded={showDetails}
              >
                <PanelRight
                  size={20}
                  className="text-primary-100"
                  aria-hidden="true"
                />
              </button>
            </div>
          ) : (
            <button
              ref={sidebarToggleRef}
              onClick={handleSidebarToggle}
              className="flex items-center justify-center bg-gray-700 text-primary-100 rounded-md p-2"
              aria-label="Hide accessibility information"
              aria-expanded={showDetails}
            >
              <ArrowRightToLine
                size={20}
                className="text-primary-100"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )}

      {/* Bottom right bar for flag icon - only visible on mobile when bottom sheet is open */}
      {showFlag && (
        <div className="fixed bottom-3 right-4 z-50 md:hidden">
          {!showDetails ? (
            <button
              onClick={() => {
                setShowDetails(true);
                // Track opening info on mobile
                trackEvent("viewedPhotoInfo", { IsMobile: true });
              }}
              className="flex items-center justify-center text-primary-100 p-2 bg-black rounded-md"
              aria-label="Accessibility info"
            >
              <Captions size={20} className="text-primary-100" />
            </button>
          ) : (
            <button
              onClick={() => {
                // Track flagging photo - updated event name
                trackEvent("flaggedPhoto");
                onFlag(currentImage);
              }}
              className="flex items-center justify-center text-primary-100 p-2 bg-black rounded-md"
              aria-label="Flag photo for review"
            >
              <PiFlagPennant
                size={20}
                className="text-primary-100"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )}

      {/* Bottom left bar for close button - only visible on mobile when bottom sheet is open */}
      <div className="fixed bottom-3 left-4 z-50 md:hidden">
        {showDetails && (
          <button
            onClick={() => {
              setShowDetails(false);
              // Track closing info on mobile
              trackEvent("hidePhotoInfo", { IsMobile: true });
            }}
            className="flex items-center justify-center text-primary-100 p-2 bg-black rounded-md"
            aria-label="Close details"
          >
            <ChevronDown size={20} className="text-primary-100" />
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left main viewer */}
        <div
          className={`flex-1 flex flex-col justify-center items-center relative overflow-hidden transition-all duration-300 ${showDetails ? "md:translate-y-0 -translate-y-[25vh]" : ""}`}
        >
          {/* Title above image */}
          <div className="text-primary-100 text-center mb-4 pt-6 md:pt-10">
            <h2 className="text-base md:text-xl text-primary-100 mb-1">
              {currentImage?.tag_name}
            </h2>
            <p className="text-xs md:text-sm text-primary-100">
              {currentImage?.category_name}
            </p>
            <p className="text-xs text-primary-100 mt-1">
              {/* Calculate subcategory-specific position */}
              {(() => {
                // Count images in the same subcategory
                const subcategoryImages = images.filter(
                  (img) =>
                    img.tag_name === currentImage?.tag_name &&
                    img.category_name === currentImage?.category_name
                );

                // Find position within subcategory
                const subcategoryPosition =
                  subcategoryImages.findIndex(
                    (img) => img.src === currentImage?.src
                  ) + 1;

                return `${subcategoryPosition} of ${subcategoryImages.length}`;
              })()}
            </p>
          </div>

          <div
            className={`w-full mx-auto flex-1 relative mb-0`}
            role="region"
            aria-label="Image gallery"
          >
            <Swiper
              modules={[Navigation, Thumbs, Zoom]}
              onSwiper={setMainSwiper}
              onSlideChange={(swiper) => {
                const newIndex = swiper.activeIndex;
                setActiveIndex(newIndex);

                // Reset zoom level when changing slides
                setCurrentZoomLevel(1);
                if (swiper.zoom && swiper.zoom.scale !== 1) {
                  swiper.zoom.out();
                }

                // Explicitly control the thumbnail swiper to center the active thumbnail
                if (thumbsSwiper && thumbsSwiper.slideTo) {
                  // Use a small timeout to ensure the DOM has updated
                  setTimeout(() => {
                    thumbsSwiper.slideTo(newIndex);
                  }, 10);
                }
              }}
              initialSlide={currentIndex}
              zoom
              thumbs={{ swiper: thumbsSwiper }}
              className="w-full h-full"
              aria-label="Main image gallery"
            >
              {images.map((img, idx) => (
                <SwiperSlide
                  key={idx}
                  className="flex justify-center items-center"
                >
                  <div className="swiper-zoom-container max-h-[75vh] w-full flex justify-center relative px-0 md:px-20">
                    {/* Image container */}
                    <div className="relative inline-flex justify-center items-center">
                      <img
                        src={img.src}
                        alt={img.alt || "Image without alt text"}
                        className="object-contain max-h-[80vh] w-full rounded-lg"
                        aria-label={img.alt || "Image without alt text"}
                        aria-hidden={idx !== activeIndex}
                        tabIndex={idx === activeIndex ? 0 : -1}
                      />

                      {/* Live region for announcing image changes */}
                      <div
                        aria-live="polite"
                        className="sr-only"
                        aria-atomic="true"
                      >
                        {idx === activeIndex && (
                          <>
                            Image {idx + 1} of {images.length}
                            {img.alt
                              ? `: ${img.alt}`
                              : ": Image without description"}
                          </>
                        )}
                      </div>

                      {/* Flag button positioned relative to the image - visible on desktop and mobile */}
                      {showFlag && (
                        <button
                          onClick={() => {
                            // Track flagging photo - updated event name
                            trackEvent("flaggedPhoto");
                            onFlag(currentImage);
                          }}
                          className={`absolute md:top-1/2 md:-left-12 top-2 left-2 md:transform md:-translate-y-1/2 z-50 text-primary-100 p-2 md:bg-gray-700 bg-black bg-opacity-70 rounded-md ${showDetails ? "md:block hidden" : ""}`}
                          aria-label="Flag photo for review"
                          aria-hidden={idx !== activeIndex}
                          tabIndex={idx === activeIndex ? 0 : -1}
                        >
                          <PiFlagPennant size={20} aria-hidden="true" />
                        </button>
                      )}

                      {/* Zoom controls on the right side of the image - hidden when bottom sheet is open on mobile */}
                      <div
                        className={`absolute md:top-1/2 md:-right-14 top-2 right-2 md:transform md:-translate-y-1/2 z-20 flex flex-col gap-2 md:gap-3 ${showDetails ? "md:flex hidden" : "flex"}`}
                        aria-hidden={idx !== activeIndex}
                      >
                        <button
                          onClick={() => {
                            if (mainSwiper && mainSwiper.zoom) {
                              mainSwiper.zoom.out();
                              setCurrentZoomLevel(1);

                              // Track reset zoom
                              trackEvent("resetZoomPhoto");
                            }
                          }}
                          className="flex items-center justify-center text-primary-100 p-2 md:bg-gray-700 bg-black bg-opacity-70 rounded-md md:mb-2"
                          aria-label="Reset image size"
                          tabIndex={idx === activeIndex ? 0 : -1}
                        >
                          <Shrink size={20} aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => {
                            if (mainSwiper && mainSwiper.zoom) {
                              const newZoomLevel = Math.min(
                                currentZoomLevel + 0.25,
                                3
                              );
                              setCurrentZoomLevel(newZoomLevel);
                              mainSwiper.zoom.in(newZoomLevel);

                              // Track zoom in
                              trackEvent("zoomedInPhoto", {
                                ZoomLevel: newZoomLevel,
                              });
                            }
                          }}
                          className="flex items-center justify-center text-primary-100 p-2 md:bg-gray-700 bg-black bg-opacity-70 rounded-md"
                          aria-label={`Zoom in to ${Math.round((currentZoomLevel + 0.25) * 100)}%`}
                          tabIndex={idx === activeIndex ? 0 : -1}
                        >
                          <ZoomIn size={20} aria-hidden="true" />
                        </button>
                        <div className="hidden md:block text-primary-100 text-xs text-center py-1">
                          {Math.round(currentZoomLevel * 100)}%
                        </div>
                        <button
                          onClick={() => {
                            if (mainSwiper && mainSwiper.zoom) {
                              const newZoomLevel = Math.max(
                                currentZoomLevel - 0.25,
                                1
                              );
                              setCurrentZoomLevel(newZoomLevel);

                              // Track zoom out
                              trackEvent("zoomedOutPhoto", {
                                ZoomLevel: newZoomLevel,
                              });

                              if (newZoomLevel === 1) {
                                const currentLevel = currentZoomLevel;
                                const steps = 4;
                                const stepSize = (currentLevel - 1) / steps;

                                for (let i = 1; i <= steps; i++) {
                                  setTimeout(() => {
                                    const stepLevel =
                                      currentLevel - stepSize * i;
                                    if (i === steps) {
                                      mainSwiper.zoom.out();
                                    } else {
                                      mainSwiper.zoom.in(stepLevel);
                                    }
                                  }, i * 30);
                                }
                              } else {
                                mainSwiper.zoom.in(newZoomLevel);
                              }
                            }
                          }}
                          className="flex items-center justify-center text-primary-100 p-2 md:bg-gray-700 bg-black bg-opacity-70 rounded-md"
                          aria-label={`Zoom out to ${Math.round((currentZoomLevel - 0.25) * 100) < 100 ? 100 : Math.round((currentZoomLevel - 0.25) * 100)}%`}
                          tabIndex={idx === activeIndex ? 0 : -1}
                        >
                          <ZoomOut size={20} aria-hidden="true" />
                        </button>
                      </div>

                      {/* Alt text button */}
                      {img.alt && (
                        <button
                          onClick={() => {
                            // Track showing alt text - updated event name
                            trackEvent("viewedPhotoAltText");
                            setShowAltText(!showAltText);
                          }}
                          className={`absolute bottom-8 right-4 z-20 text-primary-100 bg-black bg-opacity-70 rounded-full px-4 py-2 items-center gap-2 text-sm ${showAltText ? "hidden" : "hidden md:flex"}`}
                          aria-label="Show alt text"
                          aria-hidden={idx !== activeIndex || showAltText}
                          tabIndex={idx === activeIndex ? 0 : -1}
                        >
                          <Captions size={20} className="text-primary-100" />
                          <span>Show alt text</span>
                        </button>
                      )}

                      {/* Alt text display */}
                      {showAltText && img.alt && (
                        <div
                          className="absolute bottom-6 left-0 right-0 bg-black bg-opacity-70 text-primary-100 p-4 flex-col w-full hidden md:flex"
                          role="presentation"
                        >
                          <div className="flex justify-between items-start" >
                            <div className="flex-1 max-h-[150px] overflow-y-auto pr-4 custom-scrollbar">
                              <p className="font-normal text-sm text-left text-primary-100 transport-options-content-slider"
                                tabIndex={idx === activeIndex ? 0 : -1}
                                dangerouslySetInnerHTML={{ 
                                  __html: img.alt ? 
                                    img.alt.replace(/\r\n|\r|\n/g, '<br />') : '' 
                                }}
                              >
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                // Track hiding alt text - updated event name
                                trackEvent("hidePhotoAltText");
                                setShowAltText(false);
                              }}
                              className="ml-4 p-1 rounded-full hover:bg-gray-700 flex-shrink-0 hidden md:block"
                              aria-label="Hide alt text"
                              tabIndex={idx === activeIndex ? 0 : -1}
                            >
                              <EyeOff
                                size={20}
                                className="text-primary-100"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                          <div className="text-xs text-primary-100 mt-2 text-left" tabIndex={-1}>
                            {img.created_at && (
                              <>
                                {new Date(img.created_at).toLocaleDateString()}
                                <span className="mx-2">•</span>
                              </>
                            )}
                            Sociability Team
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Mobile alt text display */}
          {showAltText && currentImage?.alt && (
            <div className="md:hidden w-full px-4 mb-16">
              <div className="bg-black bg-opacity-70 rounded-md p-3">
                <div className="items-start mb-1">
                  <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                    <p className="font-normal text-sm text-left text-primary-100 transport-options-content-slider"
                      dangerouslySetInnerHTML={{ 
                        __html: currentImage.alt ? 
                          currentImage.alt.replace(/\r\n|\r|\n/g, '<br />') : '' 
                      }}
                    >
                    </p>
                  </div>
                </div>
                <div className="text-xs text-primary-100 text-left">
                  {currentImage?.created_at && (
                    <>
                      {new Date(currentImage.created_at).toLocaleDateString()}<span className="mx-2">•</span>
                    </>
                  )}
                  Sociability Team
                </div>
              </div>
            </div>
          )}

          {/* Info button for mobile - positioned at bottom left - hidden when bottom sheet is open */}
          {currentImage?.alt && !showDetails && (
            <div className="fixed bottom-3 left-4 z-50 md:hidden">
              <button
                onClick={() => {
                  // Track viewing/hiding alt text on mobile with appropriate event names
                  if (showAltText) {
                    trackEvent("hidePhotoAltText", {
                      IsMobile: true,
                    });
                  } else {
                    trackEvent("viewedPhotoAltText", {
                      IsMobile: true,
                    });
                  }
                  setShowAltText(!showAltText);
                }}
                className="flex items-center justify-center p-2 bg-black md:bg-gray-700 rounded-md"
                aria-label="Toggle alt text"
              >
                <Info size={20} className="text-primary-100" />
              </button>
            </div>
          )}

          {/* Navigation placeholder - only for desktop */}
          <div
            className="hidden md:flex justify-center items-center py-3 relative"
            aria-label="Image gallery navigation"
          >
            <div className="flex items-center space-x-4 px-6 py-2 bg-gray-700 rounded-full text-primary-100 text-sm">
              <button
                onClick={() => {
                  mainSwiper?.slidePrev();

                  // Track previous photo click - updated event name
                  trackEvent("clickedPrevPhoto");
                }}
                className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
                disabled={activeIndex === 0}
                aria-label="Previous image"
                tabIndex={0}
              >
                <ChevronLeft
                  size={20}
                  className="text-primary-100"
                  aria-hidden="true"
                />
              </button>
              <span aria-label={`Image ${activeIndex + 1} of ${images.length}`}>
                {activeIndex + 1}/{images.length}
              </span>
              <button
                onClick={() => {
                  mainSwiper?.slideNext();

                  // Track next photo click - updated event name
                  trackEvent("clickedNextPhoto");
                }}
                className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
                disabled={activeIndex === images.length - 1}
                aria-label="Next image"
                tabIndex={0}
              >
                <ChevronRight
                  size={20}
                  className="text-primary-100"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          {/* Thumbnails below - hidden on mobile */}
          <div
            className={`hidden md:block w-full md:w-2/3 px-6 pb-2 bg-black bg-opacity-70`}
            aria-hidden="true"
          >
            <div className="w-full mx-auto">
              {/* Custom styles for the scrollbar */}
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                .swiper-scrollbar {
                  position: relative !important;
                  margin-top: 8px !important;
                  height: 4px !important;
                  background: rgba(255, 255, 255, 0.1) !important;
                  border-radius: 3px !important;
                }
                .swiper-scrollbar-drag {
                  background: #A39FE8 !important;
                  border-radius: 3px !important;
                }
              `,
                }}
              />
              <div className="relative">
                <Swiper
                  modules={[Thumbs, Scrollbar, Mousewheel]}
                  onSwiper={setThumbsSwiper}
                  slidesPerView="auto"
                  spaceBetween={8}
                  watchSlidesProgress
                  centeredSlides={true}
                  slideToClickedSlide={true}
                  freeMode={false}
                  scrollbar={{ draggable: true, hide: false }}
                  mousewheel={true}
                  className="w-full mb-0"                 
                >
                  {images.map((img, idx) => (
                    <SwiperSlide
                      key={idx}
                      style={{ width: "auto", height: 50 }}
                    >
                      <img
                        src={img.src}
                        alt={img.alt || ""}
                        className={`object-contain h-full max-w-none rounded-lg border cursor-pointer ${
                          activeIndex === idx
                            ? "border-blue-500"
                            : "border-transparent"
                        }`}
                        onClick={() => {
                          mainSwiper?.slideTo(idx);
                          setActiveIndex(idx);

                          // Track thumbnail click
                          trackEvent("navigatedPhotoThumbnail", {
                            FromIndex: activeIndex,
                            ToIndex: idx,
                          });
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>

        {/* Right info panel toggleable - desktop version (side panel) */}
        {showDetails && (
          <div
            ref={sidebarRef}
            className="hidden md:block w-[320px] bg-black border-l border-gray-700 text-sm text-primary-100 overflow-y-auto relative"
            aria-label="Accessibility information panel"
          >
            {/* Header with photo name and category */}
            <div className="pt-6 pb-2 border-b border-gray-800">
              <h2 className="text-base text-primary-100 text-center">
                Accessibility info
              </h2>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b border-gray-800" role="tablist">            
              <button
                className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "accessibility" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                onClick={() => handleTabChange("accessibility")}
                role="tab"
                aria-selected={activeTab === "accessibility"}
                aria-controls="accessibility-tabpanel"
                id="accessibility-tab"
              >
                Accessibility tags
              </button>
              <button
                className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "details" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                onClick={() => handleTabChange("details")}
                role="tab"
                aria-selected={activeTab === "details"}
                aria-controls="details-tabpanel"
                id="details-tab"
              >
                Image alt text
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4">
              {/* Image details tab */}
              <div
                role="tabpanel"
                id="details-tabpanel"
                aria-labelledby="details-tab"
                hidden={activeTab !== "details"}
                tabIndex={activeTab === "details" ? 0 : -1}
                className="h-[calc(100vh-180px)] overflow-y-auto"
              >
                {activeTab === "details" && (
                  <ImageDetailsTab currentImage={currentImage} />
                )}
              </div>

              {/* Accessibility tags tab */}
              <div
                role="tabpanel"
                id="accessibility-tabpanel"
                aria-labelledby="accessibility-tab"
                hidden={activeTab !== "accessibility"}
                tabIndex={activeTab === "accessibility" ? 0 : -1}
                className="h-[calc(100vh-180px)] overflow-y-auto"
              >
                {activeTab === "accessibility" && (
                  <AccessibilityTagsTab currentImage={currentImage} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fixed mobile navigation at the bottom */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 flex justify-center items-center py-3 z-40" role="navigation" aria-label="Image gallery navigation">
          <div className="flex items-center space-x-4 px-6 py-2 bg-black rounded-full text-primary-100 text-sm">
            <button
              onClick={() => {
                mainSwiper?.slidePrev();
                
                // Track previous photo click - updated event name
                trackEvent("clickedPrevPhoto");
              }}
              className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
              disabled={activeIndex === 0}
              aria-label="Previous image"
              tabIndex={0}
            >
              <ChevronLeft size={20} className="text-primary-100" aria-hidden="true" />
            </button>
            <span aria-label={`Image ${activeIndex + 1} of ${images.length}`}>
              {activeIndex + 1}/{images.length}
            </span>
            <button
              onClick={() => {
                mainSwiper?.slideNext();
                
                // Track next photo click - updated event name
                trackEvent("clickedNextPhoto");
              }}
              className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
              disabled={activeIndex === images.length - 1}
              aria-label="Next image"
              tabIndex={0}
            >
              <ChevronRight size={20} className="text-primary-100" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Mobile bottom sheet version of info panel */}
        {showDetails && (
          <div
            className="md:hidden fixed left-0 right-0 h-[50vh] bg-black border-t border-gray-700 text-sm text-primary-100 overflow-y-auto z-30 rounded-t-xl"
            aria-label="Image details panel"
            style={{ bottom: "48px" }}
          >
            {/* Handle for bottom sheet */}
            <div className="w-full flex justify-center py-2">
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            {/* Header with photo name, category, and paging */}
            <div className="px-4 pb-3 pt-1 border-b border-gray-800">
              <div className="w-full">
                <p className="text-base text-primary-100 font-medium">
                  {currentImage?.tag_name}
                </p>
                <p className="text-xs text-primary-100">
                  {currentImage?.category_name}
                </p>
                <p className="text-xs text-primary-100 mt-1">
                  Photo {activeIndex + 1} of {images.length}
                </p>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b border-gray-800">
              <button
                className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "details" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                onClick={() => handleTabChange("details")}
              >
                Image alt text
              </button>
              <button
                className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "accessibility" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                onClick={() => handleTabChange("accessibility")}
              >
                Accessibility tags
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4">
              {" "}
              {/* No need for extra padding since navigation is now outside the sheet */}
              {/* Image details tab */}
              {activeTab === "details" && (
                <ImageDetailsTab currentImage={currentImage} />
              )}
              {/* Accessibility tags tab */}
              {activeTab === "accessibility" && (
                <AccessibilityTagsTab currentImage={currentImage} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}